import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Sparkles, ShieldAlert, Edit3 } from "lucide-react";
import { FOODS, FOOD_NOTE, FOOD_HOW_TO, FOOD_SIDE_CARDS } from "@/data/foods";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag, ElementBadge } from "@/components/common";

const HOW_TO_ICONS = [Compass, Eye, Sparkles, ShieldAlert, Edit3];

export default function Foods() {
  return (
    <div className="space-y-10">
      <PageHeader
        eyebrow="Alimentos por Elemento"
        title="Uma visão prática para consultar no dia a dia"
        subtitle="Na Medicina Tradicional Chinesa, os alimentos também podem ser observados por sua relação com elementos, sabores e órgãos. Esta página é educativa e serve como referência de consulta."
      >
        <div className="mt-3">
          <SourceTag source={{ pdfPage: 40, printedPage: "38" }} />
        </div>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Lista de Alimentos */}
        <div className="lg:col-span-8 space-y-5">
          <div className="grid gap-5">
            {FOODS.map((f) => {
              const c = elementColor(f.elemento);
              return (
                <div key={f.id} data-testid={`food-card-${f.id}`} className="overflow-hidden rounded-3xl border border-border bg-card">
                  <div className="flex items-center justify-between p-6" style={{ backgroundColor: c.soft }}>
                    <div>
                      <Link to={`/elementos/${f.elemento}`} className="font-serif text-2xl font-semibold hover:underline" style={{ color: c.text }}>{c.label}</Link>
                      <p className="text-sm" style={{ color: c.primary }}>{f.orgaos} · Sabor {f.sabor}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <ElementBadge elementKey={f.elemento} />
                      <SourceTag source={f.source} />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {f.exemplos.map((a, i) => (
                        <span key={i} className="rounded-full px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: c.soft, color: c.text }}>{a}</span>
                      ))}
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground">{f.nota}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cartões laterais do PDF: Variedade & Moderação */}
        <div className="lg:col-span-4 space-y-5">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h4 className="font-serif text-lg font-semibold text-foreground">{FOOD_SIDE_CARDS.variedade.titulo}</h4>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {FOOD_SIDE_CARDS.variedade.texto}
            </p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            <h4 className="font-serif text-lg font-semibold text-foreground">{FOOD_SIDE_CARDS.moderacao.titulo}</h4>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {FOOD_SIDE_CARDS.moderacao.texto}
            </p>
          </div>
        </div>
      </div>

      {/* Como usar */}
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <h4 className="font-serif text-xl font-medium text-foreground text-center sm:text-left">Como usar</h4>
        <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {FOOD_HOW_TO.map((passo, i) => {
            const Icon = HOW_TO_ICONS[i] || Compass;
            return (
              <div key={i} className="flex flex-col items-center sm:items-start text-center sm:text-left rounded-2xl bg-secondary/40 p-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold mb-2">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-xs text-muted-foreground">{passo}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-secondary/50 p-6 text-center">
        <p className="text-sm italic text-muted-foreground leading-relaxed">
          {FOOD_SIDE_CARDS.frase} Consulte também o{" "}
          <Link to="/sabores" className="text-gold font-medium hover:underline">guia dos sabores</Link> e os{" "}
          <Link to="/chas" className="text-gold font-medium hover:underline">chás citados <ArrowRight className="inline h-3 w-3" /></Link>.
        </p>
      </div>

      <SafetyNote>{FOOD_NOTE}</SafetyNote>
    </div>
  );
}
